/**
 * Lucide icons at the design system's drawing spec.
 *
 * design-system/ui_kits/leadmailer/icons.jsx inlines Lucide 0.462 glyphs at
 * stroke 1.7 in a 24 viewBox, 16px default. This re-exports the real package
 * with the same defaults, so every icon in the app matches the kit and we get
 * the rest of the Lucide set for the screens the kit didn't cover.
 */
import {
  AlignCenter,
  ChevronLeft,
  AlignLeft,
  AlignRight,
  AtSign,
  Ban,
  Baseline,
  Bold,
  Braces,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Code,
  Eraser,
  Heading,
  Highlighter,
  Download,
  Eye,
  Image,
  Inbox,
  Italic,
  Link2Off,
  Minus,
  MousePointerClick,
  Quote,
  Redo2,
  Strikethrough,
  Undo2,
  KeyRound,
  LayoutDashboard,
  LayoutTemplate,
  Link2,
  List,
  ListOrdered,
  Loader2,
  LogOut,
  Mail,
  Megaphone,
  Monitor,
  Moon,
  Pause,
  Pencil,
  Play,
  Plus,
  RotateCw,
  Search,
  Send,
  Shield,
  SlidersHorizontal,
  Smartphone,
  Sun,
  Trash2,
  TriangleAlert,
  Underline,
  Upload,
  Users,
  X,
  type LucideIcon,
  type LucideProps,
} from 'lucide-react'

function ds(Icon: LucideIcon) {
  const Wrapped = ({ size = 16, strokeWidth = 1.7, ...rest }: LucideProps) => (
    <Icon size={size} strokeWidth={strokeWidth} {...rest} />
  )
  Wrapped.displayName = `Ds(${Icon.displayName ?? 'Icon'})`
  return Wrapped
}

/** Named exactly as the design kit's icon map, plus the extras the app needs. */
export const Ic = {
  // from the kit
  inbox: ds(Inbox),
  megaphone: ds(Megaphone),
  template: ds(LayoutTemplate),
  users: ds(Users),
  atSign: ds(AtSign),
  list: ds(List),
  search: ds(Search),
  send: ds(Send),
  check: ds(Check),
  x: ds(X),
  clock: ds(Clock),
  plus: ds(Plus),
  upload: ds(Upload),
  moon: ds(Moon),
  sun: ds(Sun),
  mail: ds(Mail),
  eye: ds(Eye),
  retry: ds(RotateCw),
  chevronRight: ds(ChevronRight),
  sliders: ds(SlidersHorizontal),
  shield: ds(Shield),
  // added for Dashboard / Identities / Analytics / auth
  dashboard: ds(LayoutDashboard),
  chart: ds(ChartColumn),
  ban: ds(Ban),
  warning: ds(TriangleAlert),
  pencil: ds(Pencil),
  trash: ds(Trash2),
  download: ds(Download),
  spinner: ds(Loader2),
  logout: ds(LogOut),
  key: ds(KeyRound),
  play: ds(Play),
  pause: ds(Pause),
  chevronDown: ds(ChevronDown),
  // rich-text editor + preview
  bold: ds(Bold),
  italic: ds(Italic),
  underline: ds(Underline),
  textColor: ds(Baseline),
  link: ds(Link2),
  image: ds(Image),
  braces: ds(Braces),
  listUl: ds(List),
  listOl: ds(ListOrdered),
  alignLeft: ds(AlignLeft),
  alignCenter: ds(AlignCenter),
  alignRight: ds(AlignRight),
  code: ds(Code),
  chevronLeft: ds(ChevronLeft),
  undo: ds(Undo2),
  redo: ds(Redo2),
  strike: ds(Strikethrough),
  unlink: ds(Link2Off),
  quote: ds(Quote),
  heading: ds(Heading),
  hr: ds(Minus),
  clearFormat: ds(Eraser),
  highlight: ds(Highlighter),
  cta: ds(MousePointerClick),
  desktop: ds(Monitor),
  mobile: ds(Smartphone),
} as const

export type IconName = keyof typeof Ic
